import { useAuth } from '@clerk/react';
import { useLocation } from 'react-router-dom';

import { useDiffitUserContext } from '../../../contexts/DiffitUserContext';
import { SEEN_NUX_KEYS } from '../../../utils/users';
import { useDismissibleSidebarCard } from './useDismissibleSidebarCard';
import { SidebarMessage } from './SidebarMessage';
import { isSlotQuiet, SIDEBAR_SLOT_QUIET_KEY, SLOT_QUIET } from '../../../ads/slotQuiet';
import { useAdSlot } from '../../../ads/useAdSlot';
import { AD_REGISTRIES } from '../../../ads/adRegistry';
import { SidebarAdCard } from '../ads/SidebarAdCard';

/**
 * The cards that compete for AppSidebar's bottom slot, in priority order.
 * SidebarSlot renders the first eligible one; see it for the rules that hold
 * whatever this list contains.
 *
 * The order is the product ruling, not an implementation detail:
 *
 *   1. classic — a mode indicator, not onboarding. It carries the only
 *      switch-back link out of /classic, so nothing may outrank it.
 *   2. ad     — the in-app ads cascade, above welcome. Which ad (or none) is
 *      useSidebarAd's business; this rung only asks whether it produced one.
 *   3. welcome — the fallback, and the signed-out default: ads are signed-in
 *      only, so a visitor always lands on this one.
 *
 * An ordered array rather than the plain-data registry the in-resource ads use
 * (ads/adRegistry.jsx): every contender needs hooks
 * of its own, and hooks cannot be called while iterating a data array. So the
 * list is assembled here, after each contender's hooks have run unconditionally.
 * Same first-match-wins semantics, different shape, for a reason that is not
 * negotiable.
 *
 * `isResolved` is separate from `isEligible` on purpose. A contender that has
 * not decided yet is not the same as one that has decided "no": the slot holds
 * blank for the first and skips the second. Ads will resolve asynchronously, so
 * this distinction is what keeps a lower-priority card from painting first and
 * being yanked away.
 */
export function useSidebarCardLadder() {
  const { isLoaded, isSignedIn } = useAuth();
  const { diffitUser } = useDiffitUserContext();
  // /classic renders this same sidebar, and the switch-back card has to win the
  // slot there. Matched anywhere in the path so nested classic routes count.
  const { pathname } = useLocation();
  const isOnClassic = pathname.includes('/classic');

  // One key per card, so closing one does not close the other. Both hooks run
  // every render — which card is on screen must not change the hook order.
  const classic = useDismissibleSidebarCard(SEEN_NUX_KEYS.SIDEBAR_CARD_DISMISSED_WELCOME_CLASSIC);
  const welcome = useDismissibleSidebarCard(SEEN_NUX_KEYS.SIDEBAR_CARD_DISMISSED_WELCOME);

  // The ad rung owns its own dismissal and events — useAdSlot is what knows which
  // ad won, so it is what can name the nux key to write. `isResolved` matters
  // here and nowhere else: a rule can cost a round trip, and a lower-priority
  // card must not paint first and then be yanked away.
  const {
    ad,
    isResolved: isAdResolved,
    dismiss,
    trackShown,
    trackClick,
    onModalTrack,
  } = useAdSlot({
    registry: AD_REGISTRIES.sidebar,
    surface: 'sidebar',
    quietKey: SIDEBAR_SLOT_QUIET_KEY,
    standDown: pathname.includes('/p/') || pathname.includes('/project/'),
    hookName: 'sidebarAdSlot',
  });

  // Dismissal is read off the profile, so before it lands a card the teacher
  // already closed reads as undismissed. Wait, rather than flash it back at them.
  const isProfileSettled = isLoaded && (!isSignedIn || !!diffitUser);

  // Closing an ad quiets the whole slot for the tab. Holding back only the next
  // AD would let the next CARD take the vacancy in the same render, close button
  // landing where the teacher just clicked — the same swap either way.
  const isQuiet = isSlotQuiet(SLOT_QUIET.SESSION, SIDEBAR_SLOT_QUIET_KEY);

  return [
    {
      id: 'classic',
      isEligible: isOnClassic && !classic.isDismissed && !isQuiet,
      isResolved: isProfileSettled,
      render: () => <SidebarMessage variant='classic' onDismiss={classic.dismiss} />,
    },
    {
      id: 'ad',
      // The rung id is a constant, so the slot's once-per-winner guard cannot
      // tell one ad from another with it: when the winning ad changes mid-session
      // — a teacher claims the math offer and the share card replaces the offer
      // card — the new one would record no impression at all while still being
      // clickable, which reads downstream as an infinite click-through rate.
      shownKey: ad ? `ad:${ad.id}` : null,
      onShown: trackShown,
      isEligible: !!ad,
      isResolved: isProfileSettled && isAdResolved,
      render: () => (
        <SidebarAdCard ad={ad} onDismiss={dismiss} onClick={trackClick} onTrack={onModalTrack} />
      ),
    },
    {
      id: 'welcome',
      // `!isOnClassic` rather than an unconditional fallback: dismissing the
      // classic notice must leave the slot empty on /classic, not hand it to a
      // welcome card that would have nothing to do with the route.
      isEligible: !isOnClassic && !welcome.isDismissed && !isQuiet,
      isResolved: isProfileSettled,
      render: () => <SidebarMessage variant='welcome' onDismiss={welcome.dismiss} />,
    },
  ];
}
